"use client";

import React, { useRef } from "react";
import { event } from "@/lib/gtag";

type VideoProps = {
  mp4: string;
  webm?: string;
  poster?: string;
  caption?: string;
  captionsSrc?: string;
  className?: string;
  muted?: boolean;
  analytics?: {
    videoSlug?: string;
    videoTitle?: string;
    videoSubject?: string | null;
    videoContentType?: string;
    videoDurationSeconds?: number;
    sourcePage?: string;
  };
};

const PROGRESS_MILESTONES = [25, 50, 75] as const;

export default function Video({
  mp4,
  webm,
  poster,
  caption,
  captionsSrc,
  className,
  muted = true,
  analytics,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasPlayedRef = useRef(false);
  const progressMilestonesRef = useRef<Set<number>>(new Set());

  function buildVideoEventParams(progressPercent?: number) {
    const params: Record<string, unknown> = {
      video_title: analytics?.videoTitle ?? caption ?? mp4,
      video_url: mp4,
    };

    if (analytics?.videoSlug) params.video_slug = analytics.videoSlug;
    if (analytics?.videoSubject) params.video_subject = analytics.videoSubject;
    if (analytics?.videoContentType) {
      params.video_content_type = analytics.videoContentType;
    }
    if (typeof analytics?.videoDurationSeconds === "number") {
      params.video_duration_seconds = analytics.videoDurationSeconds;
    }
    if (analytics?.sourcePage) params.source_page = analytics.sourcePage;
    if (typeof progressPercent === "number") {
      params.progress_percent = progressPercent;
    }

    return params;
  }

  function handlePlay() {
    if (hasPlayedRef.current) return;
    hasPlayedRef.current = true;
    event("video_play", buildVideoEventParams());
  }

  function handleEnded() {
    event("video_complete", buildVideoEventParams());
  }

  function handleTimeUpdate(
    eventObject: React.SyntheticEvent<HTMLVideoElement>
  ) {
    const video = eventObject.currentTarget;
    const duration = analytics?.videoDurationSeconds ?? video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const progress = (video.currentTime / duration) * 100;
    for (const milestone of PROGRESS_MILESTONES) {
      if (progress < milestone) continue;
      if (progressMilestonesRef.current.has(milestone)) continue;

      progressMilestonesRef.current.add(milestone);
      event("video_progress", buildVideoEventParams(milestone));
    }
  }

  return (
    <figure className={className ? className : undefined}>
      {/* Captions are caller-provided because some clips, including this montage, contain no dialogue. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        muted={muted}
        poster={poster}
        className="w-full rounded-md shadow-sm"
        onPlay={handlePlay}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      >
        {webm ? <source src={webm} type="video/webm" /> : null}
        <source src={mp4} type="video/mp4" />
        {captionsSrc ? (
          <track
            default
            kind="captions"
            label="English"
            src={captionsSrc}
            srcLang="en"
          />
        ) : null}
      </video>
      {caption ? (
        <figcaption className="mt-2 text-sm text-gray-600">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
